import _ from 'lodash';
import { useMemo } from 'react';
import {
  AddressFieldsFragmentDoc,
  TAddressNewFieldsFragment,
  TGetAddressByIdQuery,
  TGetAddressByIdQueryVariables,
  TListAddressesByCompanyIdQuery,
  TListAddressesByCompanyIdQueryVariables,
  TListAllAddressesQuery,
  TListAllAddressesQueryVariables,
  TListOneAddressQuery,
  TListOneAddressQueryVariables,
  useGetAddressByIdLazyQuery,
  useListAddressesByCompanyIdLazyQuery,
  useListAllAddressesLazyQuery,
  useListOneAddressLazyQuery
} from '../../generated/graphql';
import { useFragmentOrFetch } from '../shared/useFragmentOrFetch';
import { useMap } from '../shared/useMap';
import { useQueryAll } from '../shared/useQueryAll';

// LISTS

export const useQueryAllAddresses = () => {
  const { data, error, loading } = useQueryAll<TListAllAddressesQuery, TListAllAddressesQueryVariables>({
    useQuery: useListAllAddressesLazyQuery
  });

  const addresses = useMemo(() => {
    return _.compact(data?.listAddresses.items);
  }, [data]);

  return {
    data: addresses,
    error,
    loading
  };
};

export const useFragmentOrFetchAddress = (variables: TGetAddressByIdQueryVariables) => {
  const { data, loading, error } = useFragmentOrFetch<TListAllAddressesQuery, TGetAddressByIdQuery, TGetAddressByIdQueryVariables>({
    fragmentDoc: AddressFieldsFragmentDoc,
    useLazyQuery: useGetAddressByIdLazyQuery,
    variables,
    __typename: 'Address'
  });

  return { data, error, loading };
};

export const useQueryAddressById = (id: string) => {
  const { data, error, loading } = useQueryAll<TGetAddressByIdQuery, TGetAddressByIdQueryVariables>({
    useQuery: useGetAddressByIdLazyQuery,
    variables: { id }
  });

  const address = useMemo(() => {
    return data?.getAddressById;
  }, [data]);

  return {
    data: address,
    error,
    loading
  };
};

export const useAllAddressesMap = () => {
  const { data, error, loading } = useQueryAllAddresses();

  const addressesMap = useMap<(typeof data)[number]>(data);

  return {
    data: addressesMap,
    error,
    loading: loading || addressesMap == null
  };
};

// =========================
// NEW
// =========================

export type TAddressType = TAddressNewFieldsFragment & { parent?: Record<string, any> };

export const useQueryOneAddress = (variables: TListOneAddressQueryVariables) => {
  const { data, error, loading } = useQueryAll<TListOneAddressQuery, TListOneAddressQueryVariables>({
    useQuery: useListOneAddressLazyQuery,
    variables
  });

  const address = useMemo(() => data?.listOneAddress.items[0] || {}, [data]);

  return {
    data: address as TAddressType,
    error,
    loading
  };
};

export const useQueryAllAddressesByCompanyId = (variables: TListAddressesByCompanyIdQueryVariables) => {
  const { data, error, loading } = useQueryAll<TListAddressesByCompanyIdQuery, TListAddressesByCompanyIdQueryVariables>({
    useQuery: useListAddressesByCompanyIdLazyQuery,
    variables
  });

  const addresses = useMemo(() => data?.listAddressesByCompanyId.items || [], [data]);

  return {
    data: addresses as TAddressType[],
    error,
    loading
  };
};
